











































































import { Cid, Post } from 'types'
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator'
import { namespace, Getter, State } from 'vuex-class'
import BeethovenAttachmentPreview from 'vuecomponents/beethoven-attachment-preview.vue'
import BeethovenAttachmentCaption from 'vuecomponents/beethoven-attachment-caption.vue'
import SurfacePostAttachmentEmbedControl from 'vuecomponents/surface_post_attachment_embed_control.vue'
import SurfacePostAttachmentButton from 'vuecomponents/surface_post_attachment_button.vue'
import { postColorRgb, serializeRgbaStringToRgba, computeLuminanceRatio } from 'bits/post_color'
import type { RGB } from 'bits/post_color'
import { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { BeethovenContentCategory, BeethovenContentSubcategory } from '@padlet/beethoven-client/lib/types'

import { addQueryParams } from 'bits/url'
import { getStylizedProviderName, downloadUrl } from 'bits/attachments'
import { IntlMixin } from 'vuemixins/intl_mixin'

const PHONE_SCREEN_WIDTH = 480
const MAX_STREAM_POST_WIDTH = 540
const MAX_CHAT_POST_WIDTH = 320
const MAX_TABLE_CELL_POST_WIDTH = 300

@Component({
  components: {
    BeethovenAttachmentPreview,
    BeethovenAttachmentCaption,
    SurfacePostAttachmentEmbedControl,
    SurfacePostAttachmentButton,
  },
})
export default class SurfacePostAttachmentNew extends Mixins(IntlMixin) {
  $el!: HTMLDivElement
  $refs!: {
    embedWrapper: HTMLDivElement
    attachmentCaption: BeethovenAttachmentCaption
  }

  @Prop() post!: Post
  @Prop() isEditableByCurrentUser!: boolean
  @Prop({ default: true }) actionable!: boolean
  @Getter format!: string
  @Getter colorScheme!: 'light' | 'dark'
  @(namespace('browser').Getter) windowWidth!: number

  isImageLoaded = false
  linkData: BeethovenDisplayAttributes | null = null
  isLoaded = false
  isFallback = false
  showMeta = false

  contentCategory: BeethovenContentCategory | null = null
  contentSubcategory: BeethovenContentSubcategory | null = null
  downloadFileName: string | null = null
  attachmentAspectRatio = 0
  attachmentColor = 'rgb(0,0,0)'
  providerName = ''
  attachmentLuminance: any
  isDownloadable = false
  attachmentEmbedCode = ''
  errorText: string | null = null

  @State isMobile!: boolean

  handleBeethovenError(error): void {
    this.errorText = error
  }

  handleDataLoaded(data): void {
    this.linkData = data
    this.isFallback = data.is_fallback
    this.contentCategory = data.content_category
    this.contentSubcategory = data.content_subcategory
    this.attachmentEmbedCode = data.embed_code
    this.attachmentAspectRatio = data.image_aspect_ratio
    this.attachmentColor = data.image_color
    this.attachmentLuminance = data.image_luminance
    this.isDownloadable = data.is_downloadable && !this.isMobile
    this.showMeta = data.show_meta
    this.providerName = getStylizedProviderName(data.provider_name)

    if (this.contentCategory === 'file') {
      this.downloadFileName = this.post.subject || null
    }

    this.$emit('load-data', data)
  }

  handleImageLoaded(data): void {
    this.isImageLoaded = true
    this.$emit('load-image', data)
  }

  get downloadUrl(): string {
    return downloadUrl(this.urlForDisplay as string)
  }

  get hasCaption(): boolean {
    return true
  }

  get url(): string | undefined {
    return this.post?.attachment
  }

  get urlForDisplay(): string | undefined {
    // Note: This should be used in display/href/... instead of this.url so if the attachment is censored (e.g. unsafe links), the bad attachment won't be exposed
    return this.linkData?.url
  }

  get captionText(): string | undefined {
    return this.post?.attachment_caption || ''
  }

  get attachmentTitle(): string {
    return this.linkData?.title || ''
  }

  get attachmentDescription(): string {
    return this.linkData?.description || ''
  }

  get backgroundColor(): RGB {
    return postColorRgb({ postColor: this.post.color, isLightColorScheme: this.colorScheme === 'light' })
  }

  get imageBackgroundColor(): RGB {
    if (!this.attachmentColor) return this.backgroundColor
    return serializeRgbaStringToRgba(this.attachmentColor)
  }

  get isShowingAttachmentBorder(): boolean {
    const luminanceRatio = computeLuminanceRatio(this.backgroundColor, this.imageBackgroundColor)
    return luminanceRatio < 1.5
  }

  postWidth = 0

  @Watch('windowWidth', { immediate: true })
  onWindowWidthChanged() {
    this.$nextTick(() => {
      this.postWidth = this.getPostWidth()
    })
  }

  getPostWidth(): number {
    const gridSize = Math.ceil(this.$el?.offsetWidth || 0)
    const MAP_GRID_SIZE = 254
    const TIMELINE_POST_WIDTH = 254
    switch (this.format) {
      case 'stream':
        if (this.windowWidth <= PHONE_SCREEN_WIDTH) {
          return gridSize
        }
        return MAX_STREAM_POST_WIDTH
      case 'grid':
        return Math.ceil(gridSize)
      case 'shelf':
        return gridSize
      case 'matrix':
        return gridSize
      case 'chat':
        if (this.windowWidth <= MAX_CHAT_POST_WIDTH) {
          return gridSize
        }
        return MAX_CHAT_POST_WIDTH
      case 'table':
        return MAX_TABLE_CELL_POST_WIDTH
      case 'map':
        return MAP_GRID_SIZE
      case 'timeline':
        return TIMELINE_POST_WIDTH
      default:
        return Math.ceil(this.post.width || 0) || 1920
    }
  }

  get attachmentWidth(): number {
    return this.postWidth - 16
  }

  get attachmentHeight(): number {
    return this.attachmentWidth / this.attachmentAspectRatio
  }

  get interaction(): 'play' | 'zoom' | 'download' | 'link' {
    if (!this.actionable) {
      return 'link'
    }
    if (this.contentCategory === 'video' || this.contentCategory === 'audio') {
      return 'play'
    }
    if (
      (this.isPhone && this.contentCategory === 'photo') ||
      (this.attachmentEmbedCode && this.contentSubcategory !== 'padlet')
    ) {
      return 'zoom'
    }
    if (this.isDownloadable) {
      return 'download'
    }
    return 'link'
  }

  get buttonIcon(): string | null {
    if (this.interaction === 'play') {
      return 'play'
    }
    if (this.interaction === 'zoom') {
      // return 'enlarge'
      return null
    }
    if (this.interaction === 'download') {
      return 'download'
    }
    return null
  }

  @(namespace('post').Action) startEditingPost!: (payload: {
    postCid: Cid
    shouldFocusAttachmentCaption: boolean
  }) => void

  openPostModal(): void {
    if (this.isEditableByCurrentUser) {
      this.startEditingPost({ postCid: this.post.cid, shouldFocusAttachmentCaption: true })
    }
  }

  // * EMBEDS AND ACTIONS

  /**
   * True -- normal behavior of showing the preview
   * False -- showing something else (etc. video player, Spotify embed)
   */
  showPreview = true
  showEmbed = false
  embedHtml: string | null = null
  embedMetaHeight = 0

  @(namespace('postAction').State) currentlyPlayingPostCid!: Cid
  @(namespace('postAction').Action) expandPost!: (payload: { cid: Cid }) => void
  @(namespace('postAction').Action) zoomPost!: (payload: {
    zoomedPostCid: Cid
    zoomedLink: BeethovenDisplayAttributes
  }) => void
  @(namespace('postAction').Action) setCurrentlyPlayingPost!: (payload: { cid: Cid }) => void
  @(namespace('postAction').Action) unsetCurrentlyPlayingPost!: (payload: { cid: Cid }) => void

  unsetAsCurrentlyPlayingPost() {
    if (this.currentlyPlayingPostCid === this.post.cid) {
      this.unsetCurrentlyPlayingPost({ cid: this.post.cid })
    }
  }

  embedInline(): void {
    this.setCurrentlyPlayingPost({ cid: this.post.cid })
    this.embedMetaHeight = (this.$refs.attachmentCaption?.$el as HTMLDivElement).offsetHeight
    this.showPreview = false
    this.showEmbed = true
    this.embedHtml = this.attachmentEmbedCode
    this.$nextTick(() => {
      const embedTag = this.$refs.embedWrapper.firstChild as Element
      if (embedTag.tagName.toLowerCase() === 'iframe') {
        const embedTagIframe = embedTag as HTMLIFrameElement
        // Set loading attributes: width, height and underlay background while it's loading
        embedTagIframe.setAttribute('width', this.attachmentWidth + 'px')
        embedTagIframe.setAttribute('height', this.attachmentHeight + 'px')
        embedTagIframe.style.background = this.attachmentColor

        // Override iframe for better behavior
        const currentSrc = embedTagIframe.getAttribute('src')
        let newSrc = addQueryParams(currentSrc, 'autoplay=1')
        if (this.providerName.toLowerCase() === 'youtube') {
          newSrc = addQueryParams(newSrc, 'rel=0')
        }
        embedTagIframe.setAttribute('src', newSrc)
        this.embedHtml = embedTagIframe.outerHTML
      }
    })
  }

  zoom() {
    if (!this.linkData) return
    this.backToPreview()
    this.zoomPost({
      zoomedPostCid: this.post?.cid,
      zoomedLink: this.linkData,
    })
  }

  backToPreview() {
    this.showPreview = true
    this.showEmbed = false
    this.unsetAsCurrentlyPlayingPost()
    // return $(this.$el).parents('.wish').removeClass('is-playing')
  }

  openInNew() {
    if (this.isApp) {
      this.openLink({
        linkType: 'external',
        url: this.urlForDisplay,
        contentCategory: this.contentCategory,
        contentSubcategory: this.contentSubcategory,
      })
    } else {
      window.open(this.urlForDisplay)
    }
  }

  @(namespace('nativeApp').Action) openLink!: (payload: { url; linkType; contentCategory; contentSubcategory }) => void
  playInApp() {
    let linkType = 'external'
    if (this.contentSubcategory === 'padlet') {
      linkType = 'padlet'
    } else if (this.contentCategory === 'photo' || this.contentCategory === 'file') {
      linkType = 'internal_mediaplayer'
    } else if (this.contentCategory === 'page' || this.contentCategory === 'document') {
      linkType = 'internal_webview'
    }

    this.openLink({
      url: this.urlForDisplay,
      linkType,
      contentCategory: this.contentCategory,
      contentSubcategory: this.contentSubcategory,
    })
  }

  @State isApp!: boolean
  @State canPostMessage!: boolean
  @State isPhone!: boolean

  play(e: MouseEvent): boolean {
    if (this.interaction === 'link' || this.interaction === 'download') {
      return true
    }

    if (this.isApp && this.canPostMessage) {
      // opening link: isApp so use postMessage to open link
      this.playInApp()
      return false
    }

    if (this.interaction === 'play') {
      // opening link: content is video and audio so we display player
      this.embedInline()
      e.preventDefault()
      return false
    }

    if (this.interaction === 'zoom') {
      // opening link: on phone, only images zoom
      // opening link: display preview for file type having the embed code
      this.zoom()
      e.preventDefault()
      return false
    }

    return true
  }
}
